/**
 * Pure aggregation for the single-entity dashboard's stat cards (dashboard
 * redesign step 2, entity/personal-single-selection scope). Every figure
 * here is composed from an existing canonical source, never re-derived:
 *   - Gross revenue YTD + net margin: GET /reports/entity-pnl summary
 *     (services/entity_pnl.get_entity_pnl), the same per-entity P&L source
 *     ProfitLossPage renders from. Routes per entity via PNL_SOURCE_REGISTRY
 *     to BR OS or Property Portfolio -- never both for the same entity.
 *   - Net assets, fixed vs current (net of their liabilities): GET
 *     /al/balance-sheet?scope=entity&entity_id=..., the same predicate the
 *     Assets & Liabilities page uses. Fixed = fixed_assets - fixed_liabilities;
 *     Current = current_assets - current_liabilities; the two sum to
 *     totals.net_assets exactly (both are subsets of the same total_assets /
 *     total_liabilities split).
 *
 * Bank Balance's per-bank sublines reuse lib/bankBalancesGrouping.js's
 * groupAccountsByBank directly (no wrapper needed here); its headline is the
 * sum of those sublines, not a separately-fetched figure, so the invariant
 * holds by construction rather than by two predicates agreeing.
 *
 * Dashboards round 2 part 3 additions (mirror the group card set, same
 * definitions computeGroupStatCards uses, never a forked formula):
 *   - Net revenue: gross revenue minus OTA commission, summed from
 *     entityPnl.months' ota_commission field (the same per-month field the
 *     P&L chart already reads).
 *   - Gross/net margin: net margin = summary.net_result (same figure the
 *     former Gross Revenue card subline already used). Gross margin adds
 *     back financing, invoice_financing, and entity_opex -- the identical
 *     "above cogs" definitions lib/entityGrossRevenueChart.js's per-row
 *     _grossMargin already uses, summed across months here for the YTD card
 *     instead of per-month.
 *   - Properties: entityPnl.property_count (Property Portfolio source only;
 *     absent/0 for BR OS and holding entities) -- the same count
 *     _portfolio_monthly_series already returns, no separate fetch.
 *
 * computeNetRevenueCard/computeMarginCards do NOT branch on entityPnl.source.
 * A single entity is always exactly one source (BR OS XOR Portfolio), so its
 * months only ever carry that source's fields (the other side's fields are
 * simply absent, contributing zero). get_business_pnl's merged "All
 * Entities" aggregate carries BOTH sets of fields simultaneously in the same
 * month -- an unconditional presence-checked sum is the one formula that is
 * correct for both cases, so there's no per-source branch to write.
 *
 * Actuals-scoped YTD (dashboards round 2 follow-up): a future-dated booking
 * is never an actual, regardless of status -- the same standing rule
 * lib/entityGrossRevenueChart.js's chart already enforces via
 * gross_revenue_actual/gross_revenue_forward (services/entity_pnl.py). Prior
 * to this fix, these four cards summed entityPnl.summary.total_revenue/
 * net_result -- the full-year total INCLUDING forward bookings -- while the
 * chart's own YTD footer summed actuals only, so the same-labelled "Gross
 * Revenue" figure silently disagreed between card and chart. Every card
 * below now sums the *_actual per-month fields (falling back to the unsplit
 * field only when the backend/fixture hasn't provided the split -- BR OS
 * months never carry a split at all, since BR OS's query already excludes
 * future dates and is therefore always fully actual). This is the same
 * predicate read directly from entityPnl.months, not a second card-side
 * filter -- see net_result_actual/gross_margin_actual/ota_commission_actual
 * in services/entity_pnl.py for where the split itself is computed. The
 * full-year (forward-inclusive) figure is not discarded: each card also
 * returns `inclToCome`, rendered as a labelled secondary line, never a
 * second percentage.
 */

// Sums a full-period field across months (no split concept -- used for
// fields that are always fully actual, e.g. BR OS revenue, whose source
// query already excludes future dates).
function _sumField(months, key) {
  return (months || []).reduce(function (sum, m) { return sum + (parseFloat(m[key]) || 0); }, 0);
}

// Sums a field that HAS an actual/forward split (services/entity_pnl.py),
// falling back to the unsplit total when the split field is absent (older
// fixture, or a source with no forward concept at all) -- the fallback
// makes "actual" and "total" coincide exactly when there is nothing to
// split, rather than silently zeroing a month.
function _sumSplit(months, actualKey, totalKey) {
  var actual = 0, total = 0;
  (months || []).forEach(function (m) {
    var t = parseFloat(m[totalKey]) || 0;
    total += t;
    actual += (m[actualKey] != null) ? (parseFloat(m[actualKey]) || 0) : t;
  });
  return { actual: actual, total: total };
}

// BR OS month revenue/COGS, actual part only. Since 2026-10-06 (#130) the
// BR OS `revenue`/`cogs` of a month include its forecast (uninvoiced share of
// live stays, invoices due later in the window), split out as
// revenue_actual/revenue_forward and cogs_forward. The merged (all-entities)
// month keeps the BR OS split as revenue_actual and bros_revenue_actual.
export function brosRevenueActual(m) {
  if (m.revenue_actual != null) return parseFloat(m.revenue_actual) || 0;
  if (m.bros_revenue_actual != null) return parseFloat(m.bros_revenue_actual) || 0;
  return parseFloat(m.revenue) || 0;
}
export function brosCogsActual(m) {
  return (parseFloat(m.cogs) || 0) - (parseFloat(m.cogs_forward) || 0);
}

// Gross revenue: BR OS splits via revenue_actual/revenue_forward; Portfolio's
// `gross_revenue` splits via gross_revenue_actual/gross_revenue_forward.
function _grossRevenueActualTotal(months) {
  var brosTotal = _sumField(months, 'revenue');
  var brosActual = (months || []).reduce(function (s, m) { return s + brosRevenueActual(m); }, 0);
  var pp = _sumSplit(months, 'gross_revenue_actual', 'gross_revenue');
  return { actual: brosActual + pp.actual, total: brosTotal + pp.total };
}

export function computeGrossRevenueCard(entityPnl) {
  var e = entityPnl || {};
  var months = e.months || [];
  var revenue = _grossRevenueActualTotal(months);
  var netMarginSplit = _sumSplit(months, 'net_result_actual', 'net_result');
  var netMarginPct = revenue.actual !== 0 ? (netMarginSplit.actual / revenue.actual) * 100 : 0;
  return {
    headline: revenue.actual,
    inclToCome: revenue.total !== revenue.actual ? revenue.total : null,
    netMargin: { amount: netMarginSplit.actual, pct: netMarginPct },
  };
}

/**
 * Net revenue (Fan, 2026-08-30): "after OTA commission and for BR OS, minus
 * the COGS / supplier payment".
 *
 * Both subtractions apply, and only one of them is ever non-zero for a given
 * entity: a portfolio entity pays OTA/channel commission and has no BR OS
 * COGS; BR Ltd pays no OTA commission and its cost of sale IS the supplier
 * payment (entity_pnl.py's `cogs` on a source='bros' month). Before this,
 * BR OS net revenue read as its full turnover -- GBP 1.5m of "net" revenue
 * against GBP 1.15m of supplier cost that was never taken off.
 */
export function computeNetRevenueCard(entityPnl) {
  var e = entityPnl || {};
  var months = e.months || [];
  var revenue = _grossRevenueActualTotal(months);
  var ota = _sumSplit(months, 'ota_commission_actual', 'ota_commission');
  var brosCogsTotal = _sumField(months, 'cogs');
  var brosCogs = (months || []).reduce(function (s, m) { return s + brosCogsActual(m); }, 0);
  var headline = revenue.actual - ota.actual - brosCogs;
  var total = revenue.total - ota.total - brosCogsTotal;
  // A148 (Fan, 2026-11-04): the backend now carries the net revenue tier
  // itself (Portfolio also after management fees, cleaning and repairs).
  // Read it whenever every month has it; the derivation above is the
  // fallback for an older payload.
  if (months.length && months.every(function (m) { return m.net_revenue != null; })) {
    headline = _sumField(months, 'net_revenue_actual');
    total = _sumField(months, 'net_revenue');
  }
  return { headline: headline, inclToCome: total !== headline ? total : null };
}

export function computeMarginCards(entityPnl) {
  var e = entityPnl || {};
  var months = e.months || [];
  var grossRevenueActual = _grossRevenueActualTotal(months).actual;

  var netMarginSplit = _sumSplit(months, 'net_result_actual', 'net_result');

  // Gross margin add-back, per month: when gross_margin_actual/net_result_actual
  // are both present (Portfolio-only or the business-merged aggregate --
  // services/entity_pnl.py's gross_margin_actual already folds in BR OS's
  // full invoice_financing/entity_opex for a merged month), their difference
  // IS the actual add-back for that month. Only a pure BR OS month (neither
  // field ever present) falls back to reading invoice_financing/entity_opex
  // directly -- using both paths for the same month would double-count the
  // BR OS side of a merged month.
  var addBackActual = months.reduce(function (sum, m) {
    if (m.gross_margin_actual != null && m.net_result_actual != null) {
      return sum + (parseFloat(m.gross_margin_actual) - parseFloat(m.net_result_actual));
    }
    return sum + (parseFloat(m.invoice_financing) || 0) + (parseFloat(m.entity_opex) || 0);
  }, 0);
  var addBackTotal = months.reduce(function (sum, m) {
    return sum
      + (parseFloat(m.invoice_financing) || 0)
      + (parseFloat(m.entity_opex) || 0)
      + (parseFloat(m.financing) || 0);
  }, 0);

  var grossMarginActual = netMarginSplit.actual + addBackActual;
  var grossMarginTotal = netMarginSplit.total + addBackTotal;
  // A148: gross margin is after the fixed costs (rent, mortgage, lease
  // voids); the backend carries it per month. Read it when every month has it.
  if (months.length && months.every(function (m) { return m.gross_margin != null && m.gross_margin_actual != null; })) {
    grossMarginActual = _sumField(months, 'gross_margin_actual');
    grossMarginTotal = _sumField(months, 'gross_margin');
  }

  return {
    grossMargin: {
      headline: grossMarginActual,
      pct: grossRevenueActual !== 0 ? (grossMarginActual / grossRevenueActual) * 100 : 0,
      inclToCome: grossMarginTotal !== grossMarginActual ? grossMarginTotal : null,
    },
    netMargin: {
      headline: netMarginSplit.actual,
      pct: grossRevenueActual !== 0 ? (netMarginSplit.actual / grossRevenueActual) * 100 : 0,
      inclToCome: netMarginSplit.total !== netMarginSplit.actual ? netMarginSplit.total : null,
    },
  };
}

export function computePropertiesCard(entityPnl) {
  var count = (entityPnl || {}).property_count || 0;
  return { headline: count, hasProperties: count > 0 };
}

// NOTE: "fixed"/"current" here is the BALANCE SHEET's STRUCTURAL split
// (fixed = properties section only; a manual al_item is always structurally
// "current", regardless of its category). This is a deliberately different
// definition from the Net Assets CHART's fixed/current split
// (services/asset_classification.classify_asset_class, ASSET-TYPE-based --
// a manual real_estate/assurance_vie/stocks/insurance item counts as
// "fixed" there). Both are individually correct by their own definition;
// they can show different numbers for the same manual item. Known,
// accepted divergence -- not a bug -- see the dashboards Net Assets
// composition-split work for context.
export function computeNetAssetsCard(balanceSheet) {
  var bs = balanceSheet || {};
  var fixedAssets = (bs.fixed_assets || {}).subtotal ?? 0;
  var fixedLiabilities = (bs.fixed_liabilities || {}).subtotal ?? 0;
  var currentAssets = (bs.current_assets || {}).subtotal ?? 0;
  var currentLiabilities = (bs.current_liabilities || {}).subtotal ?? 0;
  var netAssets = (bs.totals || {}).net_assets ?? 0;
  return {
    headline: netAssets,
    fixed: fixedAssets - fixedLiabilities,
    current: currentAssets - currentLiabilities,
  };
}
